import React, { createContext, useContext, useRef, RefObject, useCallback } from "react";

interface CartAnimationContextType {
  cartIconRef: RefObject<HTMLElement> | null;
  registerCartIcon: (element: HTMLElement | null) => void;
  triggerAnimation: (startElement: HTMLElement | null) => void;
}

const CartAnimationContext = createContext<CartAnimationContextType | undefined>(
  undefined
);

export const CartAnimationProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const cartIconRef = useRef<HTMLElement | null>(null);

  const registerCartIcon = useCallback((element: HTMLElement | null): void => {
    cartIconRef.current = element;
  }, []);

  const triggerAnimation = useCallback((startElement: HTMLElement | null): void => {
    if (!startElement || !cartIconRef.current) {
      return;
    }

    // Get positions
    const startRect = startElement.getBoundingClientRect();
    const endRect = cartIconRef.current.getBoundingClientRect();

    // Calculate start and end positions (center of elements)
    const startX = startRect.left + startRect.width / 2;
    const startY = startRect.top + startRect.height / 2;
    const endX = endRect.left + endRect.width / 2;
    const endY = endRect.top + endRect.height / 2;

    // Create particle element
    const particle = document.createElement("div");
    const particleSize = 12;
    const duration = 1500; // Slower animation (1.5 seconds)
    
    // Get secondary color from CSS variable
    const root = document.documentElement;
    const secondaryColor = getComputedStyle(root).getPropertyValue("--store-secondary").trim() || "#D1D5DB";
    
    // Convert hex to RGB for box-shadow
    const hexToRgb = (hex: string): { r: number; g: number; b: number } => {
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      return result
        ? {
            r: parseInt(result[1], 16),
            g: parseInt(result[2], 16),
            b: parseInt(result[3], 16),
          }
        : { r: 209, g: 213, b: 219 }; // Default fallback
    };
    
    const rgb = hexToRgb(secondaryColor);
    const particleColor = secondaryColor;

    particle.style.position = "fixed";
    particle.style.left = `${startX}px`;
    particle.style.top = `${startY}px`;
    particle.style.width = `${particleSize}px`;
    particle.style.height = `${particleSize}px`;
    particle.style.borderRadius = "50%";
    particle.style.backgroundColor = particleColor;
    particle.style.pointerEvents = "none";
    particle.style.zIndex = "9999";
    particle.style.transition = "none";
    particle.style.boxShadow = `0 0 8px rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.6)`;
    particle.style.transform = "translate(-50%, -50%)";

    // Add to body
    document.body.appendChild(particle);

    // Animate
    const startTime = performance.now();

    // Ease-in-out cubic function
    const easeInOut = (t: number): number => {
      return t < 0.5
        ? 4 * t * t * t
        : 1 - Math.pow(-2 * t + 2, 3) / 2;
    };

    // Calculate control point for curved path (creates an upward arc)
    const midX = (startX + endX) / 2;
    const distanceY = endY - startY;
    // Control point is above the midpoint to create an arc
    const controlY = Math.min(startY, endY) - Math.abs(distanceY) * 0.5;

    // Quadratic bezier curve interpolation
    const getBezierPoint = (t: number): { x: number; y: number } => {
      const x = (1 - t) * (1 - t) * startX + 2 * (1 - t) * t * midX + t * t * endX;
      const y = (1 - t) * (1 - t) * startY + 2 * (1 - t) * t * controlY + t * t * endY;
      return { x, y };
    };

    const animate = (currentTime: number): void => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easedProgress = easeInOut(progress);

      // Calculate current position using bezier curve
      const { x: currentX, y: currentY } = getBezierPoint(easedProgress);

      // Apply position
      particle.style.left = `${currentX}px`;
      particle.style.top = `${currentY}px`;

      // Scale down as it approaches the end
      const scale = 1 - easedProgress * 0.5;
      particle.style.transform = `translate(-50%, -50%) scale(${scale})`;
      particle.style.opacity = String(1 - easedProgress * 0.7);

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        // Animation complete - remove particle
        if (particle.parentNode) {
          particle.parentNode.removeChild(particle);
        }
      }
    };

    // Start animation
    requestAnimationFrame(animate);
  }, []);

  return (
    <CartAnimationContext.Provider
      value={{
        cartIconRef,
        registerCartIcon,
        triggerAnimation,
      }}
    >
      {children}
    </CartAnimationContext.Provider>
  );
};

export const useCartAnimationContext = (): CartAnimationContextType => {
  const context = useContext(CartAnimationContext);
  if (context === undefined) {
    throw new Error(
      "useCartAnimationContext must be used within a CartAnimationProvider"
    );
  }
  return context;
};

